import { useEffect, useState, lazy, Suspense, memo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MapContainer } from '@/components/map/MapContainer';
import { DashboardHeader } from '@/components/navigation/DashboardHeader';
import { Toaster } from '@/components/ui/Toaster';
import { useUIStore } from '@/stores/uiStore';
import { useOfflineStore } from '@/stores/offlineStore';
import { useRoutingStore } from '@/stores/routingStore';
import { useMapStore } from '@/stores/mapStore';
import { useAuthStore } from '@/stores/authStore';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useAuthBootstrap } from '@/hooks/useAuthBootstrap';
import { AuthModal } from '@/components/auth/AuthModal';
import { ResetPassword } from '@/components/auth/ResetPassword';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { tileService } from '@/services/tileService';
import { readPaymentResult } from '@/services/billingService';
import config from '@/config';

// Lazy-loaded components for code splitting
const DirectionsPanel = lazy(() => import('@/components/navigation/DirectionsPanel').then(m => ({ default: m.DirectionsPanel })));
const NavigationMode = lazy(() => import('@/components/navigation/NavigationMode').then(m => ({ default: m.NavigationMode })));
const PlaceDetails = lazy(() => import('@/components/places/PlaceDetails').then(m => ({ default: m.PlaceDetails })));
const NearbyPlaces = lazy(() => import('@/components/places/NearbyPlaces').then(m => ({ default: m.NearbyPlaces })));
const SavedPlaces = lazy(() => import('@/components/places/SavedPlaces').then(m => ({ default: m.SavedPlaces })));
const MeasureTool = lazy(() => import('@/components/tools/MeasureTool').then(m => ({ default: m.MeasureTool })));
const OfflinePanel = lazy(() => import('@/components/offline/OfflinePanel').then(m => ({ default: m.OfflinePanel })));
const StreetView = lazy(() => import('@/components/streetview/StreetView').then(m => ({ default: m.StreetView })));
const SettingsPanel = lazy(() => import('@/components/ui/SettingsPanel').then(m => ({ default: m.SettingsPanel })));
const KeyboardShortcutsHelp = lazy(() => import('@/components/ui/KeyboardShortcutsHelp').then(m => ({ default: m.KeyboardShortcutsHelp })));
const AccountPanel = lazy(() => import('@/components/account/AccountPanel').then(m => ({ default: m.AccountPanel })));
const DevelopersPanel = lazy(() => import('@/components/developers/DevelopersPanel').then(m => ({ default: m.DevelopersPanel })));
const GeofencesPanel = lazy(() => import('@/components/geofences/GeofencesPanel').then(m => ({ default: m.GeofencesPanel })));
const EmbedBuilder = lazy(() => import('@/components/business').then(m => ({ default: m.EmbedBuilder })));
const AddressPanel = lazy(() => import('@/components/address/AddressPanel').then(m => ({ default: m.AddressPanel })));
const LogisticsPanel = lazy(() => import('@/components/logistics/LogisticsPanel').then(m => ({ default: m.LogisticsPanel })));
const AdminPanel = lazy(() => import('@/components/admin/AdminPanel').then(m => ({ default: m.AdminPanel })));
const AdminConsole = lazy(() => import('@/components/admin/AdminConsole').then(m => ({ default: m.AdminConsole })));
const MatchingPanel = lazy(() => import('@/components/matching/MatchingPanel').then(m => ({ default: m.MatchingPanel })));
const DataOverlayPanel = lazy(() => import('@/components/dataviz/DataOverlayPanel').then(m => ({ default: m.DataOverlayPanel })));
const StyleStudioPanel = lazy(() => import('@/components/stylestudio/StyleStudioPanel').then(m => ({ default: m.StyleStudioPanel })));
const StaticMapPanel = lazy(() => import('@/components/staticmap/StaticMapPanel').then(m => ({ default: m.StaticMapPanel })));
const FleetPanel = lazy(() => import('@/components/fleet/FleetPanel').then(m => ({ default: m.FleetPanel })));
const DeliveryPlannerPanel = lazy(() => import('@/components/optimizer/DeliveryPlannerPanel').then(m => ({ default: m.DeliveryPlannerPanel })));

// Panel loading fallback
const PanelLoader = memo(() => (
  <div className="dashboard-panel absolute top-4 left-4 z-panel w-96 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-card p-6">
    <div className="animate-pulse space-y-3">
      <div className="h-6 bg-gray-200 rounded w-1/2" />
      <div className="h-4 bg-gray-200 rounded w-3/4" />
      <div className="h-4 bg-gray-200 rounded w-2/3" />
    </div>
  </div>
));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 2,
    },
  },
});

export default function App() {
  const { panels, setIsMobile, setIsInitializing } = useUIStore();
  const closePanel = useUIStore((s) => s.closePanel);
  const { setIsOnline, setStorageInfo } = useOfflineStore();
  const { navigation } = useRoutingStore();
  const { streetViewCoordinates, setStreetViewCoordinates } = useMapStore();
  // Server-asserted admin flag. Gates the admin route on the client (UX only —
  // the gateway enforces the real authorization on every /admin/* call).
  const isAdmin = useAuthStore((s) => s.user?.is_admin === true);

  // Password-reset deep link: the email points at /reset-password?token=...,
  // which the SPA serves (nginx history fallback). Show the reset overlay when
  // on that route; ResetPassword fires popstate when it navigates back to "/".
  const [isResetRoute, setIsResetRoute] = useState(
    typeof window !== 'undefined' && window.location.pathname === '/reset-password'
  );
  useEffect(() => {
    const sync = () => setIsResetRoute(window.location.pathname === '/reset-password');
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  // /console* is a SEPARATE full-page admin console (not an overlay on the map).
  // NB: it is NOT under /admin — nginx routes /admin* to the API gateway, so the
  // SPA page lives at /console (which falls through to the SPA history fallback).
  const [isAdminRoute, setIsAdminRoute] = useState(
    typeof window !== 'undefined' && window.location.pathname.startsWith('/console')
  );
  useEffect(() => {
    const sync = () => setIsAdminRoute(window.location.pathname.startsWith('/console'));
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  // Initialize keyboard shortcuts
  useKeyboardShortcuts();

  // Hydrate auth session + wire token-refresh / session-expired events
  useAuthBootstrap();

  // Payment return: Omniware's callback redirects back to /?payment=success|failed.
  // Surface the outcome as a toast, strip the param, and on success refresh the
  // session so the newly-activated plan is reflected without a manual re-login.
  useEffect(() => {
    const result = readPaymentResult();
    if (!result) return;
    const notify = useUIStore.getState().addNotification;
    if (result === 'success') {
      notify({ type: 'success', message: 'Payment successful — your plan is being activated.' });
      void useAuthStore.getState().refresh();
    } else if (result === 'failed') {
      notify({ type: 'error', message: 'Payment was not completed. You have not been charged.' });
    } else {
      notify({ type: 'error', message: 'We could not confirm your payment. If you were charged, contact support.' });
    }
  }, []);

  // Initialize app
  useEffect(() => {
    const init = async () => {
      // Initialize tile service
      await tileService.init();

      // Get storage info
      const storageInfo = await tileService.getStorageInfo();
      setStorageInfo(storageInfo.used, storageInfo.available);

      // Check mobile
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };
      checkMobile();
      window.addEventListener('resize', checkMobile);

      // Online/offline listeners
      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      setIsInitializing(false);

      return () => {
        window.removeEventListener('resize', checkMobile);
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    };

    init();
  }, [setIsMobile, setIsInitializing, setIsOnline, setStorageInfo]);

  // Separate full-page admin console — rendered in place of the map app.
  if (isAdminRoute) {
    return (
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-sm text-gray-500">Loading admin…</div>}>
            <AdminConsole />
          </Suspense>
          <Toaster />
        </QueryClientProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <div className="h-screen w-screen overflow-hidden bg-gray-100">
        {/* Map container - full screen */}
        <div className="absolute inset-0 z-map">
          <MapContainer />
        </div>

        {/* Dashboard header (brand + search + category chips) - top left */}
        {panels.searchPanel && !panels.directionsPanel && <DashboardHeader />}

        {/* Directions panel - top left */}
        {panels.directionsPanel && (
          <Suspense fallback={<PanelLoader />}>
            <DirectionsPanel />
          </Suspense>
        )}

        {/* Place details panel - left side */}
        {panels.placeDetailsPanel && (
          <Suspense fallback={<PanelLoader />}>
            <PlaceDetails />
          </Suspense>
        )}

        {/* Offline panel - right side (feature-flagged off by default) */}
        {config.features.offline && panels.offlinePanel && (
          <Suspense fallback={<PanelLoader />}>
            <OfflinePanel />
          </Suspense>
        )}

        {/* Nearby places panel - bottom */}
        {panels.nearbyPanel && (
          <Suspense fallback={<PanelLoader />}>
            <NearbyPlaces />
          </Suspense>
        )}

        {/* Saved places panel - left side */}
        {panels.savedPlacesPanel && (
          <Suspense fallback={<PanelLoader />}>
            <SavedPlaces />
          </Suspense>
        )}

        {/* Settings panel - right side */}
        {panels.settingsPanel && (
          <Suspense fallback={<PanelLoader />}>
            <SettingsPanel />
          </Suspense>
        )}

        {/* Measure tool panel - left side */}
        {panels.measurePanel && (
          <Suspense fallback={<PanelLoader />}>
            <MeasureTool />
          </Suspense>
        )}

        {/* Navigation mode - full screen overlay when active */}
        {navigation.isActive && (
          <Suspense fallback={null}>
            <NavigationMode />
          </Suspense>
        )}

        {/* Street View - full screen overlay */}
        {streetViewCoordinates && (
          <Suspense fallback={null}>
            <StreetView
              coordinates={streetViewCoordinates}
              onClose={() => setStreetViewCoordinates(null)}
            />
          </Suspense>
        )}

        {/* Keyboard shortcuts help */}
        {panels.shortcutsPanel && (
          <Suspense fallback={null}>
            <KeyboardShortcutsHelp />
          </Suspense>
        )}

        {/* Account panel - right side */}
        {panels.accountPanel && (
          <Suspense fallback={<PanelLoader />}>
            <AccountPanel />
          </Suspense>
        )}

        {/* Developers (API keys & usage) panel - right side, business only */}
        {panels.developersPanel && (
          <Suspense fallback={<PanelLoader />}>
            <DevelopersPanel />
          </Suspense>
        )}

        {/* Geofences management panel - right side, logged-in users */}
        {panels.geofencesPanel && (
          <Suspense fallback={<PanelLoader />}>
            <GeofencesPanel />
          </Suspense>
        )}

        {/* Embed a map / store locator builder - business only */}
        {panels.embedBuilderPanel && (
          <Suspense fallback={<PanelLoader />}>
            <EmbedBuilder onClose={() => closePanel('embedBuilderPanel')} />
          </Suspense>
        )}

        {/* Address validation / normalization - business only */}
        {panels.addressPanel && (
          <Suspense fallback={<PanelLoader />}>
            <AddressPanel onClose={() => closePanel('addressPanel')} />
          </Suspense>
        )}

        {/* Distance matrix & route optimizer - business only */}
        {panels.logisticsPanel && (
          <Suspense fallback={<PanelLoader />}>
            <LogisticsPanel onClose={() => closePanel('logisticsPanel')} />
          </Suspense>
        )}

        {/* Admin panel - admins only. Double-gated: the route only mounts when the
            server-asserted is_admin flag is true AND the panel is open. A non-admin
            can never open or see it; the /admin/* APIs are the authoritative gate. */}
        {isAdmin && panels.adminPanel && (
          <Suspense fallback={<PanelLoader />}>
            <AdminPanel />
          </Suspense>
        )}

        {/* Trace Snap — GPS-trace-to-road matching */}
        {panels.matchingPanel && (
          <Suspense fallback={<PanelLoader />}>
            <MatchingPanel onClose={() => closePanel('matchingPanel')} />
          </Suspense>
        )}

        {/* Location Insights — clustering + heatmap of uploaded points */}
        {panels.dataOverlayPanel && (
          <Suspense fallback={<PanelLoader />}>
            <DataOverlayPanel onClose={() => closePanel('dataOverlayPanel')} />
          </Suspense>
        )}

        {/* Map Branding — custom map styling */}
        {panels.styleStudioPanel && (
          <Suspense fallback={<PanelLoader />}>
            <StyleStudioPanel onClose={() => closePanel('styleStudioPanel')} />
          </Suspense>
        )}

        {/* Map Snapshots — static map image builder */}
        {panels.staticMapPanel && (
          <Suspense fallback={<PanelLoader />}>
            <StaticMapPanel onClose={() => closePanel('staticMapPanel')} />
          </Suspense>
        )}

        {/* Fleet Tracking — live asset tracking + geofence alerts */}
        {panels.fleetPanel && (
          <Suspense fallback={<PanelLoader />}>
            <FleetPanel onClose={() => closePanel('fleetPanel')} />
          </Suspense>
        )}

        {/* Delivery Planner — multi-vehicle route optimization / dispatch */}
        {panels.deliveryPlannerPanel && (
          <Suspense fallback={<PanelLoader />}>
            <DeliveryPlannerPanel onClose={() => closePanel('deliveryPlannerPanel')} />
          </Suspense>
        )}

        {/* Auth (login / sign-up) modal */}
        <AuthModal />

        {/* Password-reset page (from the reset email deep link) */}
        {isResetRoute && <ResetPassword />}

        {/* Toast notifications */}
        <Toaster />
        </div>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
